import { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import 'setimmediate';

import { Client, FeatureClient, FeatureFlagClient } from '@nakedinsurance/ff-client';
import { FeatureProvider } from '@nakedinsurance/ff-react';
import { Analytics, AppConfig, Referral, StorageType, Store } from '@nakedplatform/support';

import * as Sentry from '@sentry/browser';
import { differenceInSeconds } from 'date-fns';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ThemeProvider } from 'styled-components';

import { Modal } from 'components/modal';
import { WebAnalytics } from 'modules/analytics';
import { loadPeachOptions } from 'modules/fulfillment/services/peach';
import { ProspectProvider } from 'modules/prospects/context';
import * as zendesk from 'modules/zendesk';
import { Routes } from 'routes';
import { APP_THEME } from 'theme';

import 'assets/sass/styles.scss';
import { withAuthInDev } from 'components/auth/hoc';
import { UrlContextProvider } from 'modules/seo/context/UrlContext';

if (!global.setImmediate) {
  // @ts-ignore
  global.setImmediate = setTimeout;
}

/**
 * Render the react App
 *
 * @param props - React App props
 */
function MyApp(props: Props) {
  const { Component, err, canonicalUrl, pageProps } = props;
  const router = useRouter();
  const TOTAL_PERCENTAGE = 100;
  const QUARTER_PERCENTAGE = 25;
  const OPEN_EXPERIMENT = 'experiment_ex46_rose_on_fulfillment';
  const {
    Shared: { STAGE },
  } = Config;
  const sentryEnabled = STAGE !== 'local';
  const sentryEnvironment = getSentryEnvironment(STAGE);
  const [isZendeskLoaded, setIsZendeskLoaded] = useState<boolean>();
  const [isZendeskHidden, setIsZendeskHidden] = useState<boolean>();
  const [previousPage, setPreviousPage] = useState<PreviousPage>();
  const [client, setClient] = useState<FeatureFlagClient>();

  const isSentryInitialized = useRef(false);

  /* Initialisations that should only run once */
  useEffect(() => {
    AppConfig.initialise(Config);
    WebAnalytics.setup();

    if (!isSentryInitialized.current) {
      initialiseSentry(sentryEnvironment, sentryEnabled);
      isSentryInitialized.current = true;
    }
  }, [sentryEnvironment, sentryEnabled]);

  /**
   * Handles route changes and executes recaptcha when necessary
   *
   * @param page - URL for the current page being navigated to.
   */
  const handleOnRouteChanged = useCallback(
    (page: string) => {
      const storage = Store.getStorage(StorageType.LocalStorage);
      let firstVisitedPage: string;

      if (storage) {
        firstVisitedPage = storage.getItem('firstVisitedPage');
      }

      if (!previousPage) {
        setPreviousPage({ page, landingTime: new Date() });
        Analytics.screen(page, { isFirstTimeUser: !firstVisitedPage });
      }

      if (previousPage && previousPage.page !== page) {
        const currentTime = new Date();
        const secondsOnPreviousPage = differenceInSeconds(currentTime, previousPage.landingTime);

        Analytics.screen(page, {
          previousPage: previousPage.page,
          secondsOnPreviousPage,
          isFirstTimeUser: !firstVisitedPage,
        });
        if (storage) {
          storage.setItem('firstVisitedPage', page);
        }

        setPreviousPage({ page, landingTime: currentTime });
      }
    },
    [previousPage]
  );

  /**
   * Loads the feature flag client and the Web Effect
   */
  const configureFeatureFlagClient = useCallback(async () => {
    const success = await FeatureClient.configure({
      apiKey: Config.Web.FeatureFlags.KEY,
      key: 'naked-web',
      // localMode: true,
    });

    if (success) {
      setClient(FeatureClient);
      const value = FeatureClient.getFeatureValue(OPEN_EXPERIMENT);

      Analytics.experiment(OPEN_EXPERIMENT, value);
    }

    handleOnRouteChanged(router.basePath + router.pathname);
  }, [router.pathname, router.basePath, handleOnRouteChanged]);

  /**
   * Add event listener for route changes
   */
  useEffect(() => {
    Router.events.on('routeChangeStart', handleOnRouteChanged);

    return () => {
      Router.events.off('routeChangeStart', handleOnRouteChanged);
    };
  }, [handleOnRouteChanged]);

  /**
   * Set up feature flag client
   */
  useEffect(() => {
    configureFeatureFlagClient().catch(logDevError);
  }, [configureFeatureFlagClient]);

  /**
   * Clean feature flag resources
   */
  useEffect(() => {
    return () => {
      client?.cleanup().catch(logDevError);
    };
  }, [client]);

  /**
   * Tracks the point at which the user scrolled to
   */
  const onScroll = useCallback(() => {
    const { body, documentElement } = document;
    const { innerHeight: windowHeight, pageYOffset: scrollY } = window;
    const documentHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      documentElement.clientHeight,
      documentElement.scrollHeight,
      documentElement.offsetHeight
    );
    const scrollYPercentage =
      (Math.round(scrollY) / (documentHeight - windowHeight)) * TOTAL_PERCENTAGE;
    const shouldHideWidget =
      isZendeskLoaded &&
      router.pathname === Routes.Summary &&
      window.zE !== undefined &&
      !isZendeskHidden;

    if (!isZendeskLoaded && scrollYPercentage >= QUARTER_PERCENTAGE) {
      void zendesk.loadScript();
      setIsZendeskLoaded(true);
    } else if (shouldHideWidget) {
      setIsZendeskHidden(true);
      window.zE(() => {
        window.zE.hide();
        window.zE('webWidget:on', 'close', handleOnZendeskClose);
      });
    }
  }, [isZendeskHidden, isZendeskLoaded, router.pathname]);

  /**
   * support passive use effect
   */
  useEffect(() => {
    // Anything in here is fired on component mount.
    let supportsPassive = false;

    try {
      const opts = Object.defineProperty({}, 'passive', {
        get() {
          supportsPassive = true;

          return supportsPassive;
        },
      });

      window.addEventListener('testPassive', () => ({}), opts);
      window.removeEventListener('testPassive', () => ({}), opts);
    } catch (e) {
      console.error(e);
    }
    window.addEventListener('scroll', onScroll, supportsPassive ? { passive: true } : false);
  }, [onScroll]);

  /**
   * referal use effect
   */
  useEffect(() => {
    loadYoutube().then(assignYoutubeGlobal).catch(logDevError);
    loadPeachOptions();

    const referral = new Referral();

    referral.saveFromQuery(window.location.search);

    return () => {
      // Anything in here is fired on component unmount.
      WebAnalytics.teardown();
    };
  }, []);

  /**
   * Loads the feature flag client
   *
   * @param environment - Sentry environment to report events under
   * @param sentryEnabled - flag for enabling sentry
   */
  function initialiseSentry(environment: string, sentryEnabled: boolean) {
    if (Config?.Web?.Sentry?.DSN) {
      try {
        Sentry.init({
          enabled: sentryEnabled,
          dsn: Config.Web.Sentry.DSN,
          environment,
          replaysSessionSampleRate: 0.05, // 5% of non-error sessions are sampled
          replaysOnErrorSampleRate: 0.1, // 10% of errors are sampled
          tracesSampleRate: 0.1, // 10% of transactions are sampled
          profilesSampleRate: 0.1, // 10% of transactions are sampled
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
              maskAllInputs: true,
            }),
          ],
          tracePropagationTargets: [],
        });
      } catch (error) {
        console.error('Error initialising Sentry', error);
      }
    }
  }
  /**
   * Log error to console
   *
   * @param e - the error
   */
  function logDevError(e) {
    if (Config.Shared.STAGE === 'dev') {
      console.log(e);
    }
  }
  /**
   * Hides zendesk icon
   */
  function handleOnZendeskClose() {
    window.zE(() => window.zE.hide());
  }
  /**
   * Loads youtube iframe api
   */
  async function loadYoutube() {
    return new Promise((resolve, reject) => {
      const yt = document.createElement('script');

      yt.src = 'https://www.youtube.com/iframe_api';
      yt.type = 'text/javascript';
      yt.onerror = reject;
      yt.defer = true;
      yt.onload = resolve;
      document.head.insertBefore(
        yt,
        document.head.childNodes[document.head.childNodes.length - 1].nextSibling
      );
    });
  }
  /**
   * Create a global for youtube api instance
   */
  function assignYoutubeGlobal() {
    window.YT = YT;
  }
  return (
    <ThemeProvider theme={APP_THEME}>
      <Head>
        <meta httpEquiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="initial-scale=1.0,width=device-width" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="msapplication-TileColor" content="#fefefe" />

        <meta property="fb:app_id" content={Config.Web.Analytics.Facebook.APP_ID} />
        <meta name="application-name" content="Naked Insurance" />
        <meta name="msapplication-tooltip" content="Online insurance" />
      </Head>
      <UrlContextProvider canonicalUrl={canonicalUrl}>
        <GoogleReCaptchaProvider reCaptchaKey={Config.Web.Google.SITE_KEY} useEnterprise={true}>
          <FeatureProvider client={client}>
            <ProspectProvider>
              <Component {...pageProps} err={err} />
              <Modal />
            </ProspectProvider>
          </FeatureProvider>
        </GoogleReCaptchaProvider>
      </UrlContextProvider>
    </ThemeProvider>
  );
}
/**
 * Gets the intial props for the app component
 *
 * @param appContext - the context passed to the app by next when the page renders
 */
MyApp.getInitialProps = function (appContext: AppContext) {
  const pageContext = appContext.ctx;
  const canonicalUrl = `${Config.origin}${pageContext.pathname}`;

  return {
    canonicalUrl,
  };
};
/**
 * Normalises platform deployment stages into Sentry environments.
 *
 * @param stage - Configured platform deployment stage.
 */
function getSentryEnvironment(stage: string) {
  return stage === 'prod' ? 'production' : 'development';
}
export default withAuthInDev(MyApp);

/* Type definitions */
interface Props extends AppProps {
  err: unknown;
  /** The source-of-truth url for search engine crawlers to use when there is duplicate content between multiple URLs */
  canonicalUrl: string;
}
interface PageDetails {
  /** The name of a page */
  page: string;
  /** The time the user landed on the page */
  landingTime: Date;
}
interface State {
  /** An object containing details of the previous page */
  previousPage?: PageDetails;
  /** Flag to know if zendesk widget has been loaded  */
  isZendeskLoaded: boolean;
  /** True, if the zendesk widget is hidden */
  isZendeskHidden: boolean;
  /** FeatureFlag client */
  client: Client;
}

interface PreviousPage {
  page: string;
  landingTime: Date;
}
